import { GQL } from 'rewire-graphql';
import { utc, TimestampRange } from 'rewire-common';
import { ISubscriptionBatchedRecord, ITimecard, TimecardFilterSubscriptionInput } from '../DataTypes';
import gql from 'graphql-tag';
import { subscribe } from '../graphql/GraphQLClient';
import EntityBatchedCallbackSubscriptionService, {
  BatchedCallbackSubscriptionCallbackFn,
  EntityBatchedCallbackSubscriptionModel,
} from './EntityBatchedCallbackSubscriptionService';

const timecardsSubscription = (returnSchema: string) => gql`
  subscription($filter: TimecardFilterSubscriptionInput!) {
    timecards(filter: $filter) {
      timestamp
      records {
        operation
        timecard {
          ${returnSchema}
        }
      }
      operation
    }
  }
`;

export function subscribeToTimecards(returnSchema: string, filter: TimecardFilterSubscriptionInput, callback: any): any {
  return subscribe(timecardsSubscription(returnSchema) as GQL, { filter }, callback, 'timecards');
}

export const defaultTimecardSchema = `
  id
  name
  code
  employee {
    id
    name
  }
  effective
  earnedDate
  createdDate
  job {
    id
  }
`;

export class TimecardsBatchedCallbackSubscriptionModel extends EntityBatchedCallbackSubscriptionModel<ITimecard> {
  constructor(
    subscriptionCallback: BatchedCallbackSubscriptionCallbackFn<ITimecard>,
    options?: { returnSchema?: string; filter?: TimecardFilterSubscriptionInput },
  ) {
    super(subscriptionCallback, {
      returnSchema: options?.returnSchema || defaultTimecardSchema,
      filter: options?.filter as Record<string, unknown> | undefined,
    });
  }

  protected subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any {
    return subscribeToTimecards(returnSchema, filter, callback);
  }

  protected getSubscriptionCallback() {
    return async (timecardsBatchedRecords: ISubscriptionBatchedRecord<ITimecard>[]) => {
      if (!timecardsBatchedRecords?.length) return;
      this.sort(timecardsBatchedRecords);
      for (const br of timecardsBatchedRecords) {
        for (const r of br.records) {
          const timecard = r['timecard'] as any;
          if (timecard) {
            const createdDate = timecard.createdDate?.value;
            const earnedDate = timecard.earnedDate?.value;
            const effective = timecard.effective?.value;
            timecard.fields = typeof timecard.fields === 'string' ? JSON.parse(timecard.fields) : undefined;
            timecard.createdDate = createdDate ? utc(createdDate) : undefined;
            timecard.earnedDate = earnedDate ? utc(earnedDate) : undefined;
            timecard.effective = effective ? new TimestampRange(effective) : undefined;
          }
        }
      }
      this.subscriptionCallback?.(timecardsBatchedRecords);
    };
  }
}

class TimecardsBatchedCallbackSubscriptionService extends EntityBatchedCallbackSubscriptionService<
  ITimecard,
  TimecardsBatchedCallbackSubscriptionModel
> {
  constructor() {
    super();
  }

  protected _createSubscriptionModel(
    subscriptionCallback: BatchedCallbackSubscriptionCallbackFn<ITimecard>,
    options?: { returnSchema?: string; filter?: TimecardFilterSubscriptionInput },
  ): TimecardsBatchedCallbackSubscriptionModel {
    return new TimecardsBatchedCallbackSubscriptionModel(subscriptionCallback, options);
  }
}

const timecardsBatchedCallbackSubscriptionService = new TimecardsBatchedCallbackSubscriptionService();
export default timecardsBatchedCallbackSubscriptionService;
